'use client';

import { useEffect, useState } from 'react';
import { Code, GitBranch, Info, Layers, Terminal, Zap } from 'react-feather';
import type {
  BlogTableOfContentsIconName,
  BlogTableOfContentsItem,
} from '../../utils/blog-content.util';

const TOC_ICONS: Record<
  BlogTableOfContentsIconName,
  typeof Info
> = {
  info: Info,
  architecture: GitBranch,
  code: Code,
  terminal: Terminal,
  layers: Layers,
  zap: Zap,
};

type ArticleTableOfContentsProps = {
  items: BlogTableOfContentsItem[];
  subtitle?: string;
};

export function ArticleTableOfContents({
  items,
  subtitle = 'On this page',
}: ArticleTableOfContentsProps) {
  const [activeSectionId, setActiveSectionId] = useState(
    items[0]?.id ?? '',
  );

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const sectionElements = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);

    if (sectionElements.length === 0) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: 0,
      },
    );

    for (const sectionElement of sectionElements) {
      intersectionObserver.observe(sectionElement);
    }

    return () => {
      intersectionObserver.disconnect();
    };
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  return (
    <aside className="sticky top-24 hidden h-fit w-64 shrink-0 xl:block">
      <div className="space-y-4">
        <div>
          <h3 className="mb-1 text-2xl font-semibold text-primary">
            Table of Contents
          </h3>
          <p className="text-xs font-medium tracking-wide text-slate-500">
            {subtitle}
          </p>
        </div>
        <nav className="mt-4 space-y-2" aria-label="Blog post sections">
          {items.map((item) => {
            const IconComponent =
              TOC_ICONS[item.icon ?? 'info'] ?? TOC_ICONS.info;
            const isActive = activeSectionId === item.id;

            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={[
                  'flex items-center gap-3 border-l pl-4 transition-colors',
                  isActive
                    ? 'border-l-2 border-primary font-bold text-primary'
                    : 'border-slate-200 text-slate-500 hover:border-primary hover:text-primary',
                ].join(' ')}
              >
                <IconComponent className="size-[18px] shrink-0" aria-hidden />
                <span className="text-xs font-medium tracking-wide">
                  {item.label}
                </span>
              </a>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
